<template>
  <v-menu ref="menu" v-model="menu" :close-on-content-click="false" class="date-time-picker-menu"
    content-class="date-time-picker-menu-content" allow-overflow min-width="290px" offset-y bottom left
    :nudge-right="$attrs['text-field-prepend-icon'] ? '31px' : '0'" origin="top right" :disabled="disabled">
    <template v-slot:activator="{ on }">
      <v-text-field :value="displayValue" v-bind="textFieldAttrs" :tabindex="textFieldTabIndex" v-on="on"
        @change="onTextFieldChange" @click:clear="onTextFieldClear" @keydown.tab="closeMenu" />
    </template>
    <div>
      <div style="max-width: 290px; max-height: 378px; overflow: hidden;">
        <transition name="slide">
          <v-date-picker v-if="isDatePickerVisible" ref="datepicker" v-model="dateStr" :locale="locale"
            active-picker="activePicker" :class="['date-time-picker-date-picker']" :picker-date.sync="pickerDate"
            v-bind="datePickerAttrs" first-day-of-week="1" @input="onDatePickerInput" />
          <v-time-picker v-else ref="timepicker" v-model="timeStr" format="24hr" :class="['date-time-picker-time-picker']"
            v-bind="timePickerAttrs" @change="onTimePickerChange" />
        </transition>
      </div>
      <div v-if="!isDateOnly && !isTimeOnly">
        <v-btn block class="ma-0" depressed @click="toggleMode">
          <v-icon v-if="isDatePickerVisible" v-bind="iconAttrs">
            access_time
          </v-icon>
          <v-icon v-else v-bind="iconAttrs">
            event
          </v-icon>
        </v-btn>
      </div>
    </div>
  </v-menu>
</template>

<script>
import moment from 'moment'

export default {
  props: {
    value: {
      type: String,
      default: undefined
    },
    disabled: {
      type: Boolean,
      default: false
    },
    isDateOnly: {
      type: Boolean,
      default: false
    },
    isTimeOnly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      menu: false,
      isDatePickerVisible: true,
      isoDateTime: null,
      activePicker: 'DATE',
      pickerDate: null
    }
  },
  computed: {
    locale() {
      return this.$i18n.locale
    },

    isNullAllowed() {
      const clearable = this.$attrs['text-field-clearable']
      return clearable != null && clearable !== false
    },
    textFieldTabIndex() {
      const index = this.$attrs['text-field-tab-index']
      return index == null ? 0 : index
    },
    displayValue() {
      const dt = this.isoDateTime
      if (dt != null && dt.trim().length > 0) {
        const m = this.parseDateString(dt)
        if (m.isValid()) {
          if (this.isDateOnly) {
            return m.format('L')
          } else if (this.isTimeOnly) {
            return m.format('LT')
          } else {
            return m.format('L LT')
          }
        }
      }
      return dt
    },
    dateStr: {
      get() {
        const dt = this.isoDateTime
        const now = this.getNowAsMoment()
        if (dt == null || dt.trim().length <= 0) {
          return null
        }
        let m = this.parseDateString(dt)
        if (!m.isValid()) {
          m = now
        }
        return m.format('YYYY-MM-DD')
      },
      set(v) {
        const time = this.timeStr || '00:00:00'
        this.setIsoDateTime(v + 'T' + time)
      }
    },
    timeStr: {
      get() {
        const time = this.isoDateTime
        const now = this.getNowAsMoment()
        if (time == null || time.trim().length <= 0) {
          return null
        }
        let m = this.parseTimeString(time)
        if (!m.isValid()) {
          m = now
        }
        return m.format('HH:mm')
      },
      set(v) {
        const nv = v.replace('NaN', '00')
        const date = this.dateStr || this.getNowAsMoment().format('YYYY-MM-DD')
        this.setIsoDateTime(date + 'T' + nv + ':00')
      }
    },
    textFieldAttrs() {
      const attrs = this.$attrs
      const skipAttrs = ['value', 'counter', 'mask', 'type']
      const textFieldAttrs = this.filterAttrs(attrs, 'text-field-', skipAttrs)
      return textFieldAttrs
    },
    datePickerAttrs() {
      const attrs = this.$attrs
      const skipAttrs = ['value', 'reactive', 'type']
      const datePickerAttrs = this.filterAttrs(attrs, 'date-picker-', skipAttrs)
      return datePickerAttrs
    },
    timePickerAttrs() {
      const attrs = this.$attrs
      const skipAttrs = ['value', 'reactive', 'type']
      const timePickerAttrs = this.filterAttrs(attrs, 'time-picker-', skipAttrs)
      return timePickerAttrs
    },
    iconAttrs() {
      const attrs = this.$attrs
      const skipAttrs = []
      const timePickerAttrs = this.filterAttrs(attrs, 'icon-', skipAttrs)
      return timePickerAttrs
    }
  },
  watch: {
    value(val) {
      if (val !== this.isoDateTime) {
        this.setIsoDateTime(val)
      }
    },
    menu(val) {
      if (!val) {
        this.$nextTick(() => {
          this.onMenuClosed()
        })
      }
    },
    isoDateTime(val) {
      this.$emit('input', val)
    }
  },
  created() {
    if (this.isTimeOnly) {
      this.isDatePickerVisible = false
    }

    this.setIsoDateTime(this.value)
  },
  methods: {
    filterAttrs(attrs, prefix, disallowedAttrs) {
      const keys = Object.keys(attrs).filter(x => x.toLowerCase().indexOf(prefix) === 0)
      const result = {}
      keys.forEach(key => {
        const tfkey = key.replace(prefix, '').toLowerCase()
        if (disallowedAttrs.indexOf(tfkey) < 0) {
          result[tfkey] = attrs[key]
        }
      })

      return result
    },
    getNowAsMoment() {
      return moment().locale(this.$i18n.locale)
    },
    isIsoDateTimeValid(val) {
      const result = { isValid: false, value: null }
      const isClearable = this.isNullAllowed
      if ((val == null || val.length <= 0) && isClearable) {
        result.isValid = true
        result.value = val
      } else {
        let m = moment
        if (this.isTimeOnly) {
          m = this.parseTimeString(val)
        } else {
          m = this.parseDateString(val)
        }
        result.isValid = m == null ? false : m.isValid()
        result.value = result.isValid ? m.toISOString(true) : val
      }
      return result
    },
    onDatePickerInput() {
      if (this.isDateOnly || this.isTimeOnly) {
        this.$nextTick(() => {
          this.$refs.menu.save(this.isoDateTime)
        })
      } else {
        this.toggleMode()
      }
    },
    onTimePickerChange() {
      this.$nextTick(() => {
        this.$refs.menu.save(this.isoDateTime)
      })
    },
    onTextFieldClear() {
      if (this.isNullAllowed) {
        this.isoDateTime = this.isoDateTime == null ? '' : null
        this.$nextTick(() => {
          this.setIsoDateTime(null)
        })
      }
    },
    onTextFieldChange(e) {
      if (e != null) {
        let m = moment

        if (this.isTimeOnly) {
          e = e.includes(':') ? e : e + ':00'
          m = this.parseTimeString(e)
        } else {
          m = this.parseDateString(e)
        }
        if (m != null && m.isValid()) {
          this.isoDateTime = this.isoDateTime == null ? '' : null
          this.$nextTick(() => {
            this.setIsoDateTime(m.toISOString(true))
          })
        } else if (m == null && this.isNullAllowed) {
          this.onTextFieldClear()
        } else {
          const v = this.value
          this.isoDateTime = this.isoDateTime == null ? '' : null
          this.$nextTick(() => {
            this.setIsoDateTime(v)
          })
        }
      }
    },
    toggleMode() {
      this.isDatePickerVisible = !this.isDatePickerVisible
    },
    closeMenu() {
      this.menu = false
    },
    onMenuClosed() {
      if (this.isTimeOnly) {
        this.isDatePickerVisible = false
      } else {
        this.isDatePickerVisible = true
      }
      if (this.$refs.timepicker) {
        this.$refs.timepicker.selectingHour = true
      }
      if (!this.isTimeOnly) {
        if (this.$refs.datepicker) {
          this.activePicker = 'DATE'
        }
      }
    },
    parseDateString(str) {
      if (str == null || str.length <= 0) {
        return null
      }
      const m = moment
      const locale = this.$i18n.locale
      let res = m(str, ['L LT', 'L LTS', 'l LT', 'l LTS', 'L', 'l'], locale, true)
      if (!res.isValid()) {
        // couldn't parse with local format try iso8601 / rfc2822
        res = m(str).locale(locale)
      }
      return res
    },
    parseTimeString(str) {
      if (str == null || str.length <= 0) {
        return null
      }
      const m = moment
      const locale = this.$i18n.locale
      let res = m(str, ['LT', 'LTS'], locale, true)
      if (!res.isValid()) {
        res = m(str)
      }
      return res
    },
    setDefaultValue() {
      if (this.isNullAllowed) {
        this.isoDateTime = null
      } else {
        this.isoDateTime = this.getNowAsMoment().toISOString(true)
      }
    },
    setIsoDateTime(val) {
      const validationResult = this.isIsoDateTimeValid(val)
      if (validationResult.isValid) {
        if (this.isoDateTime !== validationResult.value) {
          this.isoDateTime = validationResult.value
        }
      } else {
        this.setDefaultValue()
      }
    }
  }
}
</script>

<style lang="scss">
.date-time-picker-menu-content {
  .date-time-picker-date-picker {

    .v-picker__body {
      height: 290px;
    }
  }

  .date-time-picker-time-picker {

    .v-time-picker-title__time {

      .v-picker__title__btn,
      span {
        height: 56px;
        font-size: 56px;
      }
    }
  }

  .date-time-picker-date-picker,
  .date-time-picker-time-picker {
    transform: translateY(0px);

    &.slide-leave-active,
    &.slide-enter-active {
      transition: all .25s;
    }
  }

  .date-time-picker-date-picker {

    &.slide-enter {
      transform: translateY(-756px);
    }

    &.slide-enter-to,
    &.slide-leave-to {
      transform: translateY(-378px);
    }
  }

  .date-time-picker-time-picker {

    &.slide-leave,
    &.slide-enter {
      transform: translateY(0px);
    }

    &.slide-leave-to {
      transform: translateY(378px);
    }

    &.slide-enter-to {
      transform: translateY(-378px);
    }
  }

  .date-holiday {
    color: red;
    font-weight: 900;
  }
}
</style>
